import { useUIStore } from 'Modules/ui/index.store';
import { useCallback, useEffect, useState } from 'react';
import useEntityPanel from './useEntityPanel';
import useHistoryDetailsPanel from './useHistoryDetailsPanel';
import useSoftphonePanel from './useSoftphonePanel';
import useVideoSummaryPanel from './useVideoSummaryPanel';

type PanelType =
  | 'entity'
  | 'entityOrContactLoading'
  | 'call'
  | 'dialpad'
  | 'historyDetails'
  | 'videoSummary';

interface UsePanelOrderParams {
  isNewSoftphoneEnabled?: boolean;
}

/**
 * Manages the order of the panels.
 */
const usePanelOrder = ({ isNewSoftphoneEnabled }: UsePanelOrderParams = {}) => {
  const { entitySidePanelInfo } = useUIStore();

  const [panelOrder, setPanelOrder] = useState<PanelType[]>([]);

  const { isOpen: isEntityPanelOpen } = useEntityPanel();
  const { isDialpadOpen: isDialpadPanelOpen, isCallOpen: isCallPanelOpen } =
    useSoftphonePanel({ isNewSoftphoneEnabled });
  const { isOpen: isHistoryDetailsPanelOpen } = useHistoryDetailsPanel();
  const { isOpen: isVideoSummaryPanelOpen } = useVideoSummaryPanel();
  /**
   * Inserts a panel into the panel order.
   * Always gives priority to the call panel.
   */
  const insertPanel = useCallback((panelType: PanelType) => {
    if (!panelType) {
      return;
    }

    setPanelOrder((oldOrder) => {
      const newPanelOrder = [...oldOrder];

      if (oldOrder.includes(panelType)) {
        newPanelOrder.splice(newPanelOrder.indexOf(panelType), 1);
      }

      if (panelType === 'call') {
        newPanelOrder.unshift(panelType);
        return newPanelOrder;
      }

      if (newPanelOrder.includes('call')) {
        newPanelOrder.splice(newPanelOrder.indexOf('call') + 1, 0, panelType);
      } else {
        newPanelOrder.unshift(panelType);
      }

      return newPanelOrder;
    });
  }, []);

  /**
   * Removes a panel from the panel order.
   */
  const removePanel = useCallback((panelType: PanelType) => {
    if (!panelType) {
      return;
    }

    setPanelOrder((oldOrder) => {
      if (!oldOrder.includes(panelType)) {
        return oldOrder;
      }

      const newPanelOrder = [...oldOrder];
      newPanelOrder.splice(newPanelOrder.indexOf(panelType), 1);
      return newPanelOrder;
    });
  }, []);

  useEffect(() => {
    if (isCallPanelOpen) {
      insertPanel('call');
    } else {
      removePanel('call');
    }
  }, [isCallPanelOpen, insertPanel, removePanel]);

  useEffect(() => {
    if (isDialpadPanelOpen) {
      insertPanel('dialpad');
    } else {
      removePanel('dialpad');
    }
  }, [isDialpadPanelOpen, insertPanel, removePanel]);

  useEffect(() => {
    if (isEntityPanelOpen) {
      insertPanel('entity');
    } else {
      removePanel('entity');
    }
  }, [isEntityPanelOpen, insertPanel, removePanel]);

  useEffect(() => {
    if (entitySidePanelInfo?.isLoading) {
      insertPanel('entityOrContactLoading');
    } else {
      removePanel('entityOrContactLoading');
    }
  }, [entitySidePanelInfo?.isLoading, insertPanel, removePanel]);

  useEffect(() => {
    if (isHistoryDetailsPanelOpen) {
      insertPanel('historyDetails');
    } else {
      removePanel('historyDetails');
    }
  }, [isHistoryDetailsPanelOpen, insertPanel, removePanel]);

  useEffect(() => {
    if (isVideoSummaryPanelOpen) {
      insertPanel('videoSummary');
    } else {
      removePanel('videoSummary');
    }
  }, [isVideoSummaryPanelOpen, insertPanel, removePanel]);
  return panelOrder;
};

export default usePanelOrder;
